import { z } from 'zod'

export const RuntimeBudgetErrorCode = Object.freeze({
  noDates: 'error.campaign.config.no_dates',
  remoteCampaignNotFound: 'error.campaign.config.remote_campaign_not_found',
  platformAccountNotLinked: 'error.campaign.config.platform_account_not_linked',
  timezoneMismatch: 'error.campaign.config.timezone_mismatch',
  campaignEnded: 'error.campaign.config.campaign_ended',
  dailyBudgetTooLow: 'error.campaign.config.daily_budget.too_low',
  endDateInvalid: 'error.campaign.config.end_date.invalid',
  // Live spend fetch failed on POST/preview. `data.platform` says which
  // platform. spendPermissionDenied is recoverable by reconnecting the asset;
  // spendFetchFailed is transient / on our side.
  spendPermissionDenied: 'error.campaign.config.spend_permission_denied',
  spendFetchFailed: 'error.campaign.config.spend_fetch_failed',
  // Ad account metadata (currency/timezone/minimum) lookup failed on POST
  accountMetadataPermissionDenied: 'error.campaign.config.account_metadata_permission_denied',
  accountMetadataFetchFailed: 'error.campaign.config.account_metadata_fetch_failed',
  // GET only: returned in the 200 body's error_code when the preview could not
  // be computed for a non-permission reason.
  runtimeBudgetUnavailable: 'error.campaign.config.runtime_budget_unavailable',
})

// Per-platform breakdown — each active platform's own share
// of total_budget/spend/daily_budget, plus its own runtime. Values are
// partner currency, same as the top-level aggregate fields they sum into.
const platformBudgetBreakdownSchema = z.object({
  platform: z.string(),
  total_budget: z.number(),
  spend: z.number(),
  runtime_days: z.number(),
  daily_budget: z.number(),
  // This platform's own daily minimum; 0 when it enforces none (Display-only
  // Google campaigns), absent on responses from before it was added.
  min_daily_budget: z.number().nullable().optional(),
})

const taskBudgetEntrySchema = z
  .object({
    platform: z.string(),
    new_total_budget: z.number(),
    currency: z.string(),
  })
  .passthrough()

// GET response's task_details (utils/budget_runtime_helpers.py::format_response,
// read from DynamoDB table "RuntimeBudgetDetails") — richer than the raw IW
// dispatch response returned inline by POST (see updateTaskDetailsSchema
// below). {} (no fields) when no task has ever run for this event.
const taskDetailsSchema = z
  .object({
    task_id: z.string().optional(),
    status: z.string().optional(),
    datetime: z.string().nullable().optional(),
    reason: z.string().optional(),
    args: z
      .object({
        budget: z
          .union([z.number(), z.array(taskBudgetEntrySchema)])
          .nullable()
          .optional(),
        // Single operator-facing total in partner currency — present for the
        // multi-platform path, absent for the legacy scalar path.
        total_budget: z.number().nullable().optional(),
        start_date: z.string().nullable().optional(),
        end_date: z.string().nullable().optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough()
  .nullable()

export const runtimeBudgetResponseSchema = z
  .object({
    total_budget: z.number().nullable().optional(),
    start_date: z.string().nullable().optional(),
    end_date: z.string(),
    new_daily_budget: z.number().nullable().optional(),
    // Sum of the active platforms' own daily floors
    min_daily_budget: z.number().nullable().optional(),
    min_budget: z.number().nullable().optional(),
    max_budget: z.number().nullable().optional(),
    currency: z.string().nullable().optional(),
    platforms: z.array(platformBudgetBreakdownSchema).nullable().optional(),
    timezone: z.string().nullable().optional(),
    task_details: taskDetailsSchema.optional(),
    has_permission_error: z.boolean().optional(),
    // Which platform ('fb'|'google') the permission error came from, when
    // has_permission_error is true. null for a non-permission spend failure.
    permission_error_platform: z.string().nullable().optional(),
    // Non-permission reason the figures are missing; null when they were computed.
    error_code: z.string().nullable().optional(),
  })
  .passthrough()

// Degrades to null on fetch failure.
const updateTaskDetailsSchema = z
  .object({
    task_id: z.string().optional(),
    status: z.string().optional(),
  })
  .passthrough()
  .nullable()

export const runtimeBudgetUpdateResponseSchema = z
  .object({
    new_start_date: z.string(),
    new_end_date: z.string(),
    new_daily_budget: z.number().nullable().optional(),
    currency: z.string().nullable().optional(),
    platforms: z.array(platformBudgetBreakdownSchema).nullable().optional(),
    timezone: z.string().nullable().optional(),
    task_details: updateTaskDetailsSchema.optional(),
  })
  .passthrough()
